import React from "react";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InputFake from "../GeneralComponents/InputFake";
import InputRut from "../GeneralComponents/InputRut";
import InputFile from "../GeneralComponents/InputFile";
import SignatureFooter from "../GeneralComponents/SignatureFooter";
import FileDownload from "@mui/icons-material/FileDownload";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import { Button, InputAdornment } from "@mui/material";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageButtonSimple from "../GeneralComponents/PageButtonSimple";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import {
  DAYS,
  DEFAULT_ITEMS_CONTINGENCIAS,
} from "../../Constants/Constants";
import {
  parseBoldText,
  getValueByKey,
  toSnakeCase,
} from "../utils/general_utils";

// dayjs para parsear strings y aceptar también dayjs directamente
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const toDayjs = (value, formats) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value;
  const d = dayjs(value, formats, "es", true);
  return d.isValid() ? d : null;
};

export default function ContingenciasHiddenPreview({
  formData,
  typeContingencia,
  textContingencia = "",
  evidence = null,
  children = null,
  id = null,
  embedded = false, // <- NUEVO: modo embebido para usar con drawer
}) {
  const datos = formData ?? {};

  const es_no_programada = datos?.contingencia_programada === false;
  const es_rbd = (datos?.extension ?? "rbd") === "rbd";
  const es_externa = String(datos?.especifica_origen_falla ?? "")
    .toUpperCase()
    .includes("EXTERNA");

  const max = Math.max(
    datos.ees_managers?.length ?? 0,
    datos.ees_managers_email?.length ?? 0
  );
  const countArray = max > 0 ? max : 0;

  const mineducBlocked = es_rbd ? es_no_programada && es_externa : true;

  // Valores para pickers (aceptan string o dayjs)
  const dtOcurrencia = toDayjs(
    datos.especifica_fecha_hora_ocurrencia,
    ["YYYY-MM-DDTHH:mm:ss", "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss"]
  );
  const dtResolucion = toDayjs(
    datos.especifica_fecha_hora_resolucion,
    ["YYYY-MM-DDTHH:mm:ss", "YYYY/MM/DD HH:mm:ss", "YYYY-MM-DD HH:mm:ss"]
  );
  const dateInicioProg = toDayjs(
    datos.programada_inicio_contingencia,
    ["YYYY-MM-DD", "YYYY/MM/DD"]
  );
  const dateFinProg = toDayjs(
    datos.programada_fin_contingencia,
    ["YYYY-MM-DD", "YYYY/MM/DD"]
  );
  const timeInicioProg = toDayjs(datos.programada_hora_inicio, ["HH:mm:ss"]);
  const timeFinProg = toDayjs(datos.programada_hora_termino, ["HH:mm:ss"]);

  const tituloContingencia =
    typeContingencia === "ESPECIFICA" && !es_externa
      ? getValueByKey(DEFAULT_ITEMS_CONTINGENCIAS, "id", "3", "title")
      : getValueByKey(
          DEFAULT_ITEMS_CONTINGENCIAS,
          "type",
          typeContingencia,
          "title"
        );

  const mainContent = (
    <Box sx={{ width: "100%" }}>
      <Typography
        variant="h1"
        color="inherit"
        noWrap
        sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
      >
        {tituloContingencia}
      </Typography>

      <Stack alignItems="left" justifyContent="center" spacing={2}>
        <Box
          justifyContent="center"
          sx={{ display: "flex", margin: "0.2rem!important" }}
        >
          <Stack>
            {(() => {
              const hasId = id != null && String(id) !== "";
              const sizes = hasId
                ? { label: ".8875rem", value: ".8875rem" }
                : { label: ".6875rem", value: ".6875rem" };

              return (
                <>
                  <Typography
                    component="div"
                    color="inherit"
                    sx={{
                      textAlign: "center",
                      justifyContent: "center",
                      py: 0,
                      mt: 0,
                      fontSize: sizes.label,
                    }}
                  >
                    N° folio:{" "}
                    <Typography
                      component="span"
                      sx={{
                        pl: "1rem",
                        mt: 0,
                        fontSize: sizes.value,
                        color: hasId ? undefined : "#FFF",
                      }}
                    >
                      {String(id ?? "").padStart(8, "0")}
                    </Typography>
                  </Typography>

                  <Typography
                    component="div"
                    color="inherit"
                    sx={{
                      textAlign: "center",
                      justifyContent: "center",
                      py: 0,
                      mt: 0,
                      fontSize: sizes.label,
                    }}
                  >
                    Fecha de creación:{" "}
                    <Typography
                      component="span"
                      sx={{
                        pl: "1rem",
                        mt: 0,
                        fontSize: sizes.value,
                      }}
                    >
                      {(datos.fecha_creacion || new Date().toISOString()).slice(
                        0,
                        10
                      )}
                    </Typography>
                  </Typography>
                </>
              );
            })()}
          </Stack>
        </Box>

        <Box
          justifyContent="center"
          sx={{ maxWidth: "100%", margin: "0 auto", display: "flex" }}
        >
          <Typography
            variant="body"
            align="center"
            sx={{ marginBottom: "2rem" }}
          >
            {parseBoldText(textContingencia)}
          </Typography>
        </Box>
      </Stack>

      <form id="contingencia">
        <Stack spacing={4} sx={{ marginBottom: "2rem" }}>
          {/* DATOS DEL EMISOR */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS DEL EMISOR
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("rut_emisor")}
                  disabled
                  required
                  name="rut"
                  label="Rut Operador Administrador"
                  size="small"
                  value={datos.rut_emisor}
                />
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("name_emisor")}
                  disabled
                  required
                  name="nombre"
                  label="Nombre y Apellido"
                  size="small"
                  value={datos.name_emisor}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* DATOS DEL OPERADOR */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS DEL OPERADOR
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("id_operador")}
                  disabled
                  required
                  name="id_operador"
                  label="ID Operador"
                  size="small"
                  value={datos.id_operador}
                />
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl fullWidth variant="outlined">
                <TextField
                  test_id={toSnakeCase("operator_name")}
                  disabled
                  required
                  name="operator_name"
                  label="Nombre Operador"
                  size="small"
                  value={datos.operator_name}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* DATOS BASE CONTINGENCIA */}
          <Typography variant="h3" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            DATOS BASE DE CONTINGENCIA
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl sx={{ width: "100%" }} size="small">
                <InputLabel
                  id="especifica_origen_falla-label"
                  sx={{ backgroundColor: "#fff" }}
                >
                  Origen del corte
                </InputLabel>
                <Select
                  test_id={toSnakeCase("especifica_origen_falla")}
                  disabled
                  required
                  labelId="especifica_origen_falla-label"
                  id="especifica_origen_falla"
                  value={datos.especifica_origen_falla}
                  label="Origen del corte"
                  size="small"
                  sx={{ width: "100%" }}
                >
                  <MenuItem
                    test_id={toSnakeCase("menu_especifica_origen_falla_0")}
                    value={"Falla interna al adjudicatario"}
                  >
                    Evento interno al establecimiento
                  </MenuItem>
                  <MenuItem
                    test_id={toSnakeCase("menu_especifica_origen_falla_1")}
                    value={"Falla externa al adjudicatario"}
                  >
                    Evento externo al establecimiento
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {typeContingencia !== "PROGRAMADA" && (
              <Grid size={12}>
                <FormControl sx={{ width: "100%" }} size="small">
                  <InputLabel
                    id="especifica_codigo_evento-label"
                    sx={{ backgroundColor: "#fff" }}
                  >
                    Código de evento
                  </InputLabel>
                  <Select
                    test_id={toSnakeCase("especifica_codigo_evento")}
                    disabled
                    required
                    labelId="especifica_codigo_evento-label"
                    id="especifica_codigo_evento"
                    value={datos.especifica_codigo_evento}
                    label="Código de evento"
                    size="small"
                    sx={{ width: "100%" }}
                  >
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_1")}
                      value={"1"}
                    >
                      1 - Falla física equipo de datos cliente
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_2")}
                      value={"2"}
                    >
                      2 - Bloqueo equipo de datos cliente
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_3")}
                      value={"3"}
                    >
                      3 - Corte enlace físico (Fibra óptica o cobre)
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_4")}
                      value={"4"}
                    >
                      4 - Corte enlace microondas
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_5")}
                      value={"5"}
                    >
                      5 - Caída plataforma satelital
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_6")}
                      value={"6"}
                    >
                      6 - Caída enlace internacional
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_7")}
                      value={"7"}
                    >
                      7 - Falla nodo de red
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_8")}
                      value={"8"}
                    >
                      8 - Falla red de transporte
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_9")}
                      value={"9"}
                    >
                      9 - Error de configuración firewall
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_10")}
                      value={"10"}
                    >
                      10 - Error de configuración DNS
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_11")}
                      value={"11"}
                    >
                      11 - Error de configuración otro nodo de servicio
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_12")}
                      value={"12"}
                    >
                      12 - Falla de equipo firewall
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_13")}
                      value={"13"}
                    >
                      13 - Falla de equipo DNS
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_14")}
                      value={"14"}
                    >
                      14 - Falla de equipo otro nodo de servicio
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_15")}
                      value={"15"}
                    >
                      15 - Corte de energía EES
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_16")}
                      value={"16"}
                    >
                      16 - Corte suministro público energía
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_17")}
                      value={"17"}
                    >
                      17 - Otro responsabilidad del operador
                    </MenuItem>
                    <MenuItem
                      test_id={toSnakeCase("menu_especifica_codigo_evento_18")}
                      value={"18"}
                    >
                      18 - Otro responsabilidad de terceros
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {/* DATOS ZONA (ZONAL) */}
          {typeContingencia === "ZONAL" && (
            <>
              <Typography
                variant="h3"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1 }}
              >
                DATOS DE LA ZONA
              </Typography>
              <Grid container spacing={2}>
                <Grid size={9}>
                  <FormControl sx={{ minWidth: "100%" }}>
                    <TextField
                      test_id={toSnakeCase("Zona")}
                      disabled
                      id="outlined-required"
                      label="Zona"
                      size="small"
                      sx={{ width: "100%" }}
                      value={""}
                    />
                  </FormControl>
                </Grid>
                <Grid item size={3}>
                  <PageButtonSimple
                    pad=".5rem"
                    width="auto"
                    startIcon={<AddCircleOutlineOutlinedIcon />}
                    disabled
                  >
                    Agregar a la lista
                  </PageButtonSimple>
                </Grid>

                <Grid size={12}>
                  <TableContainer component={Paper} sx={{ mb: 2, mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "80px" }}>
                            <IconButton test_id={toSnakeCase("delete")}>
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell>Id Zona</TableCell>
                          <TableCell>RBDs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Array.isArray(datos.zonas) &&
                        datos.zonas.length > 0 ? (
                          datos.zonas.map((zona, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <IconButton
                                  test_id={toSnakeCase("delete_" + index)}
                                >
                                  <RemoveCircleOutlineIcon />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "middle" }}>
                                {zona.id_zona}
                              </TableCell>
                              <TableCell sx={{ verticalAlign: "middle" }}>
                                {Array.isArray(zona.rbd_list)
                                  ? zona.rbd_list.join(", ")
                                  : ""}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No hay zonas agregadas
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </>
          )}

          {/* DATOS RBD (PROGRAMADA / ESPECIFICA) */}
          {(typeContingencia === "PROGRAMADA" ||
            typeContingencia === "ESPECIFICA") && (
            <>
              <Typography
                variant="h3"
                color="inherit"
                noWrap
                sx={{ flexGrow: 1 }}
              >
                DATOS DEL RBD
              </Typography>

              <Grid container spacing={2}>
                <Grid size={6}>
                  <FormControl sx={{ minWidth: "100%" }}>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <TextField
                          test_id={toSnakeCase("rbd")}
                          disabled
                          id="outlined-required"
                          label="RBD"
                          size="small"
                          sx={{ width: "100%" }}
                          value={datos.rbd}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>

                <Grid size={6}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      test_id={toSnakeCase("id_zona")}
                      label="ID Zona"
                      size="small"
                      disabled
                      value={datos.id_zona}
                      sx={{ width: "100%" }}
                    />
                  </FormControl>
                </Grid>

                <Grid size={6}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      test_id={toSnakeCase("region")}
                      label="Región"
                      size="small"
                      disabled
                      value={datos.region}
                      sx={{ width: "100%" }}
                    />
                  </FormControl>
                </Grid>

                <Grid size={6}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      test_id={toSnakeCase("comuna")}
                      label="Comuna"
                      size="small"
                      disabled
                      value={datos.comuna}
                      sx={{ width: "100%" }}
                    />
                  </FormControl>
                </Grid>

                <Grid size={12}>
                  <FormControl fullWidth variant="outlined">
                    <TextField
                      test_id={toSnakeCase("nombre_colegio")}
                      id="outlined"
                      label="Nombre del Colegio"
                      size="small"
                      disabled
                      value={datos.nombre_colegio}
                      sx={{ width: "100%" }}
                    />
                  </FormControl>
                </Grid>

                {Array.from({ length: countArray }).map((_, i) => (
                  <React.Fragment key={i}>
                    <Grid size={6}>
                      <TextField
                        test_id={toSnakeCase(`ees_managers_${i}`)}
                        label={`Representante del establecimiento ${i + 1}`}
                        size="small"
                        disabled
                        value={datos?.ees_managers?.[i] ?? ""}
                        sx={{ width: "100%" }}
                      />
                    </Grid>

                    <Grid size={6}>
                      <TextField
                        test_id={toSnakeCase(`ees_managers_email_${i}`)}
                        label={`Email Representante del establecimiento ${
                          i + 1
                        }`}
                        size="small"
                        disabled
                        value={datos?.ees_managers_email?.[i] ?? ""}
                        sx={{ width: "100%" }}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>

              {datos.especifica_origen_falla ===
                "Falla interna al adjudicatario" && (
                <Grid container spacing={2}>
                  <Grid sx={{ width: "100%", marginY: 2 }} size={12}>
                    <Box>
                      <InputRut
                        errorRut={() => {}}
                        handleRut={() => {}}
                        handleEmail={() => {}}
                        handleNombre={() => {}}
                        handleApellido={() => {}}
                        margin="0"
                        checked={datos.checked}
                        rut={datos.ees_temporal_manager}
                        email={datos.ees_temporal_manager_email}
                        nombre_temp={datos.ees_temporal_manager_nombres}
                        apellido_temp={datos.ees_temporal_manager_apellidos}
                      ></InputRut>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* DETALLE CONTINGENCIA */}
          <Typography variant="h3" color="inherit" noWrap sx={{}}>
            DETALLE DE LA CONTINGENCIA
          </Typography>

          {/* PROGRAMADA */}
          {typeContingencia === "PROGRAMADA" && (
            <FormControl sx={{ width: "100%", spacing: 2 }}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <InputFake
                    test_id={toSnakeCase("programada_descripcion")}
                    value={datos.programada_descripcion}
                  ></InputFake>
                </Grid>

                <Grid size={12}>
                  <Typography size="body">
                    Rango horario de ocurrencia
                  </Typography>
                  <Divider></Divider>
                </Grid>

                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                  >
                    <TimePicker
                      test_id={toSnakeCase("programada_hora_inicio")}
                      disabled
                      sx={{ width: "100%" }}
                      label="Hora de inicio"
                      value={timeInicioProg}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                  >
                    <TimePicker
                      test_id={toSnakeCase("programada_hora_termino")}
                      disabled
                      sx={{ width: "100%" }}
                      label="Hora de término"
                      value={timeFinProg}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid size={12}>
                  <Typography size="body">
                    Repetición de la ocurrencia
                  </Typography>
                  <Divider></Divider>
                </Grid>

                <Grid size={12}>
                  <Grid container spacing={2} columns={14}>
                    {DAYS.map((dia) => (
                      <Grid size={2} key={dia}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              test_id={toSnakeCase(dia)}
                              checked={!!datos?.days?.[dia]}
                              name={dia}
                            />
                          }
                          label={dia}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <Typography size="body">
                    Periodo que debe durar la contingencia programada
                  </Typography>
                  <Divider></Divider>
                </Grid>

                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"es"}
                  >
                    <DatePicker
                      test_id={toSnakeCase("programada_inicio_contingencia")}
                      disabled
                      sx={{ width: "100%" }}
                      label="Fecha de inicio periodo"
                      value={dateInicioProg}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"es"}
                  >
                    <DatePicker
                      test_id={toSnakeCase("programada_fin_contingencia")}
                      disabled
                      sx={{ width: "100%" }}
                      label="Fecha de término periodo"
                      value={dateFinProg}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </FormControl>
          )}

          {/* ZONAL / ESPECIFICA */}
          {(typeContingencia === "ZONAL" ||
            typeContingencia === "ESPECIFICA") && (
            <>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"es"}
                  >
                    <DateTimePicker
                      test_id={toSnakeCase(
                        "especifica_fecha_hora_ocurrencia"
                      )}
                      disabled
                      sx={{ width: "100%" }}
                      label="Fecha y hora de ocurrencia"
                      value={dtOcurrencia}
                      slotProps={{
                        textField: {
                          size: "small",
                        },
                      }}
                      disableFuture
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid size={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={"es"}
                  >
                    <DateTimePicker
                      test_id={toSnakeCase(
                        "especifica_fecha_hora_resolucion"
                      )}
                      disabled
                      sx={{ width: "100%" }}
                      label="Fecha y hora de resolución"
                      value={dtResolucion}
                      slotProps={{
                        textField: {
                          size: "small",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <InputFake
                test_id={toSnakeCase("especifica_descripcion_falla")}
                value={datos.especifica_descripcion_falla}
              ></InputFake>
            </>
          )}

          {/* EVIDENCIA */}
          {evidence && typeContingencia !== "PROGRAMADA" ? (
            <Box>
              <InputFile
                onFileSelect={() => {}}
                initialFile={evidence}
                fileType="pdf"
                placeholder="Subir evidencia (formato PDF)"
              />
              <Typography
                variant="small"
                align="left"
                sx={{ marginBottom: "5px", color: "#111" }}
              ></Typography>
              <Typography
                variant="small"
                align="left"
                sx={{
                  mt: "5px",
                  display: "block",
                }}
              >
                *Atención: Se recomienda que las fotografías dentro de la
                evidencia vengan georeferenciadas (marca de fecha y lugar en la
                que se tomó) esto será validado por SUBTEL y no por la
                plataforma SAGEC.
              </Typography>
            </Box>
          ) : typeContingencia !== "PROGRAMADA" ? (
            <>
              <Grid container spacing={2}>
                <Grid size={8}>
                  <TextField
                    test_id={toSnakeCase("evidence")}
                    disabled
                    size="small"
                    variant="outlined"
                    placeholder="Subir evidencia (formato PDF)"
                    value={
                      Array.isArray(datos.evidence)
                        ? datos.evidence.join(", ")
                        : datos.evidence || ""
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{ marginLeft: "10px" }}
                        >
                          <ImageSearchIcon sx={{ color: "black" }} />
                        </InputAdornment>
                      ),
                      readOnly: true,
                    }}
                    sx={{ width: "100%", maxWidth: "100%" }}
                    readOnly
                  />
                </Grid>
                <Grid size={4}>
                  <Button
                    test_id={toSnakeCase("Descargar evidencia")}
                    fullWidth
                    variant="text"
                    disabled={
                      Array.isArray(datos.evidence) &&
                      datos.evidence.length > 0
                        ? false
                        : true
                    }
                    color="primary"
                    onClick={() =>
                      window.open(datos.evidence?.[0], "_blank")
                    }
                    startIcon={<FileDownload />}
                  >
                    Descargar evidencia
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : null}
        </Stack>
      </form>
    </Box>
  );

  return (
    <>
      {embedded ? (
        // Versión embebida (para drawer): sin PageBgBlock ni márgenes grandes
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
            p: 2,
          }}
        >
          {mainContent}
        </Box>
      ) : (
        // Versión original de página completa
        <PageBgBlock pad={id ? "0" : "2rem 9rem"}>{mainContent}</PageBgBlock>
      )}

      {children}

      {/* Sólo mostramos el footer de firma en la versión de página completa */}
      {!id && !embedded ? (
        <SignatureFooter
          mineduc={datos.checked || !mineducBlocked}
        ></SignatureFooter>
      ) : null}
    </>
  );
}
